import {$isListItemNode} from '@lexical/list';
import styles from 'components/DSYS/editor/plugins/Comment.module.css';
import {
  clearCommentThreadHoverHighlights,
  emitCommentThreadHover,
  getMostSpecificActiveHighlightId,
  getThreadsForElement,
  setCommentThreadHoverHighlight,
} from 'components/DSYS/editor/utils/highlightHelpers';
import {
  $applyNodeReplacement,
  $isElementNode,
  $isTextNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementNode,
  LexicalNode,
  NodeKey,
  RangeSelection,
  SerializedElementNode,
  Spread,
} from 'lexical';

export type SerializedHighlightNode = Spread<
  {
    ids?: string[]; // Array of thread IDs (to support overlapping comments)
    className?: string;
  },
  SerializedElementNode
>;

const EPHEMERAL_HIGHLIGHT_CLASSES = new Set([
  styles.comment,
  styles.focused,
  styles.highlighted,
  styles.commenting,
  'comment',
  'focused',
  'highlighted',
  'commenting',
]);

const EPHEMERAL_HIGHLIGHT_CLASS_PATTERNS = [
  /^Comment_comment__/,
  /^Comment_focused__/,
  /^Comment_highlighted__/,
  /^Comment_commenting__/,
];

export const isEphemeralHighlightClassToken = (classToken: string) =>
  EPHEMERAL_HIGHLIGHT_CLASSES.has(classToken) ||
  EPHEMERAL_HIGHLIGHT_CLASS_PATTERNS.some((pattern) => pattern.test(classToken));

export const sanitizeHighlightClassName = (className?: string) => {
  if (!className) {
    return undefined;
  }

  const retainedTokens = className
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((token) => !isEphemeralHighlightClassToken(token));

  return retainedTokens.length > 0 ? retainedTokens.join(' ') : undefined;
};

export class HighlightNode extends ElementNode {
  __ids: string[];
  __className?: string;

  static getType(): string {
    return 'highlight-mark';
  }

  static clone(node: HighlightNode): HighlightNode {
    return new HighlightNode(node.__ids, node.__className, node.__key);
  }

  constructor(highlightIds: string | string[] | undefined, className?: string, key?: NodeKey) {
    super(key);

    if (!highlightIds) {
      this.__ids = [];
      this.__className = className;
      return;
    }

    // Normalize to array and filter out empty/falsy values
    const idsArray = Array.isArray(highlightIds) ? highlightIds : [highlightIds];
    this.__ids = idsArray.filter((id) => id && id.trim());
    this.__className = className;
  }

  // Make this node inline so it behaves like MarkNode
  isInline(): boolean {
    return true;
  }

  // Remove the node if it becomes empty (no children or only empty text)
  canBeEmpty(): boolean {
    return false;
  }

  // Extract children with the node to maintain structure during operations
  extractWithChild(): boolean {
    return true;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        const highlightId = domNode.getAttribute('data-highlight-id');
        if (!highlightId) {
          return null;
        }
        return {
          conversion: convertHighlightElement,
          priority: 1,
        };
      },
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');

    // Add class based on number of threads (for styling overlaps)
    const baseClass = this.__className || 'highlight';
    span.classList.add(baseClass);
    if (this.__ids.length > 1) {
      span.classList.add(styles.multiHighlighted);
    }

    if (this.__ids.length > 0) {
      span.setAttribute('data-highlight-id', this.__ids.join(' '));
    }

    // For hover effect
    span.addEventListener('mouseover', (event) => this.handleMouseOver(event));
    span.addEventListener('mouseout', (event) => this.handleMouseOut(event));

    return span;
  }

  updateDOM(prevNode: HighlightNode): boolean {
    // Check if we need to update the DOM
    const idsChanged =
      prevNode.__ids.length !== this.__ids.length ||
      prevNode.__ids.some((id, i) => id !== this.__ids[i]);
    const classChanged = prevNode.__className !== this.__className;

    return idsChanged || classChanged;
  }

  exportDOM(): DOMExportOutput {
    const span = document.createElement('span');
    span.classList.add(this.__className || 'highlight');
    if (this.__ids.length > 1) {
      span.classList.add(styles.multiHighlighted);
    }

    // Only set attribute if we have IDs
    if (this.__ids.length > 0) {
      span.setAttribute('data-highlight-id', this.__ids.join(' '));
    }
    span.setAttribute('data-lexical-type', 'highlight-mark');
    const serializedClassName = sanitizeHighlightClassName(this.__className);
    if (serializedClassName) {
      span.setAttribute('data-highlight-class', serializedClassName);
    }

    return {
      element: span,
    };
  }

  handleMouseOver(event: MouseEvent) {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && event.currentTarget instanceof Node) {
      if (event.currentTarget.contains(relatedTarget)) {
        return;
      }
    }

    const threadId = this.getHoveredCommentThreadIdFromEventTarget(target);
    if (!threadId) {
      return;
    }

    this.highlightCommentThread(threadId);
  }

  handleMouseOut(event: MouseEvent) {
    const threadId = this.getHoveredCommentThreadIdFromEventTarget(event.target);
    if (threadId) {
      this.clearCommentThreadHover(threadId);
    }

    const nextThreadId = this.getHoveredCommentThreadIdFromEventTarget(event.relatedTarget);
    if (nextThreadId) {
      this.highlightCommentThread(nextThreadId);
    }
  }

  private getHoveredCommentThreadIdFromEventTarget(target?: EventTarget | null): string | null {
    if (!(target instanceof Node)) {
      return null;
    }

    const element =
      target.nodeType === Node.ELEMENT_NODE
        ? (target as HTMLElement)
        : (target.parentElement as HTMLElement | null);
    if (!element) {
      return null;
    }

    const commentElement = this.findClosestCommentElement(element);
    if (!commentElement) {
      return null;
    }

    const threads = getThreadsForElement(commentElement);
    return getMostSpecificActiveHighlightId(commentElement, threads);
  }

  private findClosestCommentElement(element: HTMLElement): HTMLElement | null {
    return element.closest<HTMLElement>('span[data-highlight-id]');
  }

  private highlightCommentThread(threadId: string) {
    clearCommentThreadHoverHighlights();
    setCommentThreadHoverHighlight(threadId, true);
    emitCommentThreadHover(threadId);
  }

  private clearCommentThreadHover(threadId: string) {
    setCommentThreadHoverHighlight(threadId, false);
    emitCommentThreadHover(undefined);
  }

  getHighlightIds(): string[] {
    return this.__ids;
  }

  hasHighlightId(id: string): boolean {
    return this.__ids.includes(id);
  }

  // Method to add a thread ID to this node (for overlaps)
  addHighlightId(id: string): void {
    if (!this.__ids.includes(id)) {
      const writable = this.getWritable();
      writable.__ids = [...this.__ids, id];
    }
  }

  // Method to remove a thread ID from this node
  removeHighlightId(id: string): void {
    const writable = this.getWritable();
    writable.__ids = this.__ids.filter((existingId) => existingId !== id);
  }

  exportJSON(): SerializedHighlightNode {
    return {
      ...super.exportJSON(),
      type: 'highlight-mark',
      version: 1,
      ids: this.__ids,
      className: sanitizeHighlightClassName(this.__className),
    };
  }

  static importJSON(serializedNode: SerializedHighlightNode): HighlightNode {
    const ids = serializedNode.ids || [];
    return new HighlightNode(ids, sanitizeHighlightClassName(serializedNode.className));
  }

  insertNewAfter(_: RangeSelection, restoreSelection = true): HighlightNode {
    const highlightNode = $createHighlightNode(this.__ids, this.__className);
    this.insertAfter(highlightNode, restoreSelection);
    return highlightNode;
  }
}

function convertHighlightElement(domNode: HTMLElement): DOMConversionOutput | null {
  const highlightIdStr = domNode.getAttribute('data-highlight-id');
  if (!highlightIdStr) {
    return null;
  }

  const highlightIds = highlightIdStr.split(' ').filter(Boolean);
  if (highlightIds.length === 0) {
    return null;
  }

  const className = sanitizeHighlightClassName(
    domNode.getAttribute('data-highlight-class') ||
      (domNode.classList.contains(styles.comment) ? styles.comment : undefined)
  );

  const node = $createHighlightNode(highlightIds, className);

  return {
    node,
  };
}

export function $createHighlightNode(ids: string | string[], className?: string): HighlightNode {
  return $applyNodeReplacement(new HighlightNode(ids, className));
}

export const $wrapSelectionInHighlightNode = (
  selection: RangeSelection,
  id: string,
  className?: string
): HighlightNode | null => {
  if (selection.isCollapsed()) {
    return null;
  }

  // Note: selection.extract() splits text nodes at selection boundaries and returns
  // the extracted nodes. It may return ListItemNodes if entire list items are selected,
  // or it may return just the split text nodes if the selection is partial across list items.
  const nodes = selection.extract();

  if (nodes.length === 0) {
    return null;
  }

  // Check if we have any text nodes in the extracted nodes.
  // If we do, we should skip ListItemNodes because the text nodes are already
  // correctly split. If we only have ListItemNodes and no text nodes, then
  // we need to wrap their children.
  const hasTextNodes = nodes.some((n) => $isTextNode(n));

  // Process each extracted node
  let firstCreatedNode: HighlightNode | null = null;

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const parent = node.getParent();

    // If this node is already inside a HighlightNode, we can't just add the new ID
    // to the parent — that will incorrectly expand the highlight range
    if (parent && $isHighlightNode(parent)) {
      // If selection.extract() gives us an existing HighlightNode here, do NOT wrap it.
      // That can incorrectly add the new comment ID to a wider range than the user selected.
      if ($isHighlightNode(node)) {
        continue;
      }

      const parentIds = parent.getHighlightIds();
      const parentClassName = parent.__className;

      // Instead, wrap this node with a new HighlightNode that combines
      // the parent's IDs with the new one
      const newHighlight = $createHighlightNode(
        Array.from(new Set([...parentIds, id])), // normalize ids
        parentClassName
      );
      node.replace(newHighlight);
      newHighlight.append(node);

      if (!firstCreatedNode) {
        firstCreatedNode = newHighlight;
      }
      continue;
    }

    if ($isHighlightNode(node)) {
      // Skip *nested* HighlightNodes from `selection.extract()`; merging the new ID
      // into them can incorrectly widen the highlight range
      if (parent) {
        continue;
      }

      // This is an existing HighlightNode - add our ID to it (merge)
      node.addHighlightId(id);

      if (!firstCreatedNode) {
        firstCreatedNode = node;
      }
    } else if ($isTextNode(node)) {
      // This is a text node - wrap it in a new HighlightNode
      const highlightNode = $createHighlightNode(id, className);
      node.replace(highlightNode);
      highlightNode.append(node);

      if (!firstCreatedNode) {
        firstCreatedNode = highlightNode;
      }
    } else if ($isElementNode(node)) {
      // For block-level container nodes (like ListItemNode), we can't wrap them
      // in an inline span - that creates invalid HTML. Instead, wrap their children.
      if ($isListItemNode(node)) {
        // If extract() returned text nodes, those are already split correctly at selection
        // boundaries. Skip the ListItemNode because wrapping all its children would
        // incorrectly highlight text outside the selection. Only process ListItemNode children
        // if extract() gave us NO text nodes at all (meaning entire list items were selected).
        if (hasTextNodes) {
          continue;
        }

        // Entire list item is selected - wrap all its children
        const children = node.getChildren();
        for (const child of children) {
          if ($isTextNode(child)) {
            const highlightNode = $createHighlightNode(id, className);
            child.replace(highlightNode);
            highlightNode.append(child);

            if (!firstCreatedNode) {
              firstCreatedNode = highlightNode;
            }
          } else if ($isHighlightNode(child)) {
            child.addHighlightId(id);
            if (!firstCreatedNode) {
              firstCreatedNode = child;
            }
          } else if ($isElementNode(child) && child.isInline()) {
            // Inline element children can be wrapped
            const highlightNode = $createHighlightNode(id, className);
            child.replace(highlightNode);
            highlightNode.append(child);

            if (!firstCreatedNode) {
              firstCreatedNode = highlightNode;
            }
          }
        }
      } else if (node.isInline()) {
        // For inline element nodes, wrap them directly
        const highlightNode = $createHighlightNode(id, className);
        node.replace(highlightNode);
        highlightNode.append(node);

        if (!firstCreatedNode) {
          firstCreatedNode = highlightNode;
        }
      }
    }
  }

  return firstCreatedNode;
};

export function $isHighlightNode(node: LexicalNode | null | undefined): node is HighlightNode {
  return node instanceof HighlightNode;
}
